import { OBJExporter } from "three/examples/jsm/exporters/OBJExporter";

const MIME_EXT = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };
const STEM = "model";

// Parse a GLB ArrayBuffer into its glTF JSON + binary chunk.
// GLB layout: 12-byte header, then chunks [uint32 length, uint32 type, data].
// Chunk type 0x4E4F534A = "JSON", 0x004E4942 = "BIN".
function parseGlb(glb) {
  const view = new DataView(glb);
  if (view.getUint32(0, true) !== 0x46546c67) return null; // not "glTF"
  let offset = 12;
  let json = null;
  let bin = null;
  while (offset < view.byteLength) {
    const length = view.getUint32(offset, true);
    const type = view.getUint32(offset + 4, true);
    const start = offset + 8;
    if (type === 0x4e4f534a) {
      json = JSON.parse(new TextDecoder().decode(new Uint8Array(glb, start, length)));
    } else if (type === 0x004e4942) {
      bin = new Uint8Array(glb, start, length);
    }
    offset = start + length;
  }
  return json ? { json, bin } : null;
}

// Image indices used as a base-color map by any material — the ones we paint
// as map_Kd. Other embedded images are ignored.
function baseColorImageIndices({ json }) {
  const textures = json.textures || [];
  const indices = new Set();
  (json.materials || []).forEach((m) => {
    const idx = m.pbrMetallicRoughness?.baseColorTexture?.index;
    if (idx != null && textures[idx]?.source != null) indices.add(textures[idx].source);
  });
  return indices;
}

// Original bytes + mime of one glTF image embedded via bufferView.
// A URI-based (non-embedded) image returns null — caller falls back to canvas.
function extractEmbeddedImage({ json, bin }, imageIndex) {
  const img = (json.images || [])[imageIndex];
  if (!img || img.bufferView == null || !bin) return null;
  const bv = (json.bufferViews || [])[img.bufferView];
  const byteOffset = bv.byteOffset || 0;
  const bytes = bin.slice(byteOffset, byteOffset + bv.byteLength);
  const mimeType = img.mimeType || "image/png";
  return { blob: new Blob([bytes], { type: mimeType }), mimeType };
}

// Canvas fallback: rasterize a live Three texture map to a PNG blob.
async function textureToPngBlob(map) {
  const image = map?.image;
  if (!image) return null;
  const canvas = document.createElement("canvas");
  canvas.width = image.width || image.videoWidth;
  canvas.height = image.height || image.videoHeight;
  canvas.getContext("2d").drawImage(image, 0, 0);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

function collectMaterials(object3D) {
  const seen = new Set();
  const materials = [];
  object3D.traverse((child) => {
    if (!child.isMesh || !child.material) return;
    const list = Array.isArray(child.material) ? child.material : [child.material];
    list.forEach((m) => {
      if (seen.has(m.uuid)) return;
      seen.add(m.uuid);
      materials.push(m);
    });
  });
  return materials;
}

function colorLine(material) {
  const c = material.color;
  return c ? `Kd ${c.r.toFixed(6)} ${c.g.toFixed(6)} ${c.b.toFixed(6)}` : "Kd 1 1 1";
}

/**
 * Flip texture V for the duration of fn, then restore.
 *
 * Every image this writer emits has its first row at the top: the atlas is
 * copied out of the GLB verbatim (glTF mandates a top-left origin) and panel
 * maps arrive pre-baked to the same convention (bakeTextureTransform). OBJ puts
 * v = 0 at the *bottom*, so every textured mesh needs the same one-time flip.
 *
 * Geometries can be shared between meshes — flipping one twice would cancel out,
 * hence the dedupe on the attribute itself.
 */
function withObjUVs(object3D, fn) {
  const saved = [];
  const seen = new Set();
  object3D.traverse((child) => {
    const uv = child.isMesh && child.geometry?.attributes?.uv;
    if (!uv || seen.has(uv)) return;
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    if (!mats.some((m) => m && m.map)) return;
    seen.add(uv);
    saved.push({ uv, original: uv.array.slice() });
    for (let i = 0; i < uv.count; i++) uv.setY(i, 1 - uv.getY(i));
  });
  try {
    return fn();
  } finally {
    saved.forEach(({ uv, original }) => uv.array.set(original));
  }
}

/**
 * `atlasOwners` — optional Set of material uuids that came from the downloaded
 * GLB. Only those materials reuse the GLB's atlas bytes; other materials
 * (PV panels) rasterize their own live map.
 */
export async function buildObjFiles({ object3D, glb, atlasOwners }, folder) {
  const materials = collectMaterials(object3D);

  // Give every material a stable, unique name so `usemtl`/`newmtl` line up.
  const usedNames = new Set();
  materials.forEach((m, i) => {
    let name = m.name || `material_${i}`;
    while (usedNames.has(name)) name = `${name}_${i}`;
    m.name = name;
    usedNames.add(name);
  });

  // A material owns the atlas when it came from the downloaded GLB; with no
  // atlasOwners set (bare model export) every material qualifies.
  const ownsAtlas = (material) => !atlasOwners || atlasOwners.has(material.uuid);

  // Geometry (.obj) — reflects the model's current in-scene transform.
  let objText = withObjUVs(object3D, () => new OBJExporter().parse(object3D));
  objText = `mtllib ${STEM}.mtl\n${objText}`;

  // Reuse the GLB atlas's original bytes when its materials share one base-color
  // image; other materials rasterize their live map to a PNG.
  const parsed = glb ? parseGlb(glb) : null;
  let soleAtlas = null;
  if (parsed) {
    const used = baseColorImageIndices(parsed);
    if (used.size === 1) soleAtlas = extractEmbeddedImage(parsed, [...used][0]);
  }

  const files = [];
  const imageFileByMatName = new Map();
  const pngByTextureUuid = new Map(); // dedupe canvas re-encodes of a shared map
  let atlasFileName = null;
  let counter = 0;

  for (const material of materials) {
    if (!material.map) continue; // colour-only material — no image
    let fileName;
    if (soleAtlas && ownsAtlas(material)) {
      // Emit the shared atlas once (exact original bytes), reuse thereafter.
      if (!atlasFileName) {
        atlasFileName = `${STEM}_${counter}.${MIME_EXT[soleAtlas.mimeType] || "png"}`;
        counter += 1;
        files.push({ name: `${folder}/${atlasFileName}`, blob: soleAtlas.blob });
      }
      fileName = atlasFileName;
    } else {
      const uuid = material.map.uuid;
      if (pngByTextureUuid.has(uuid)) {
        fileName = pngByTextureUuid.get(uuid);
      } else {
        const blob = await textureToPngBlob(material.map);
        if (!blob) continue;
        fileName = `${STEM}_${counter}.png`;
        counter += 1;
        files.push({ name: `${folder}/${fileName}`, blob });
        pngByTextureUuid.set(uuid, fileName);
      }
    }
    imageFileByMatName.set(material.name, fileName);
  }

  // Material library (.mtl).
  const mtlText = materials
    .map((m) => {
      const lines = [`newmtl ${m.name}`, colorLine(m)];
      // Panels render at 0.5 alpha in the viewer, which is what lightens them
      // over the roof. `d` is the MTL dissolve (1 = opaque); `Tr` is the same
      // value inverted, written for the readers that only understand that one.
      const alpha = m.transparent && m.opacity != null ? m.opacity : 1;
      if (alpha < 1) {
        lines.push(`d ${alpha.toFixed(6)}`, `Tr ${(1 - alpha).toFixed(6)}`);
      }
      const img = imageFileByMatName.get(m.name);
      if (img) lines.push(`map_Kd ${img}`);
      return lines.join("\n");
    })
    .join("\n\n");

  return [
    { name: `${folder}/${STEM}.obj`, blob: new Blob([objText], { type: "text/plain" }) },
    { name: `${folder}/${STEM}.mtl`, blob: new Blob([mtlText], { type: "text/plain" }) },
    ...files,
  ];
}
